export const BATCH_PLANNING_HORIZON_DAYS = 90

import type {
  AppointmentPlanningCommitPayload,
  AppointmentPlanningPreviewPayload,
  AppointmentPlanningPreviewRow,
  AppointmentPlanningStrategy,
  FlexAppointmentPlanningMode,
} from "../../../../../../api/kehrblatt/planning"
import type {
  AppointmentPlanningExistingSourceRow,
  AppointmentPlanningWorkOrderOverride,
} from "../../../_logic/appointments-contract"
import { formatDateOnly } from "../../../_logic/dates"
import {
  BATCH_PLANNING_MAX_ROWS,
  batchPlanningRowLimit,
  validateBatchPlanningRowLimit,
} from "../../../_logic/demo-batch-planning-limit"

export { BATCH_PLANNING_MAX_ROWS, batchPlanningRowLimit, validateBatchPlanningRowLimit }

const resolveRowFlexAppointmentMode = ({
  override,
  defaultFlexAppointmentAllowed,
  defaultFlexAppointmentMode,
  flexPlanningAvailable,
}: {
  override?: AppointmentPlanningWorkOrderOverride | null
  defaultFlexAppointmentAllowed: boolean
  defaultFlexAppointmentMode?: FlexAppointmentPlanningMode
  flexPlanningAvailable: boolean
}): FlexAppointmentPlanningMode => {
  if (!flexPlanningAvailable) return "DISABLED"

  const explicitMode = override?.flex_appointment_mode

  if (explicitMode === "ALL" || explicitMode === "EVERY_SECOND" || explicitMode === "DISABLED") {
    return explicitMode
  }

  if (typeof override?.flex_appointment_allowed === "boolean") {
    return override.flex_appointment_allowed ? "ALL" : "DISABLED"
  }

  return defaultFlexAppointmentMode ?? (defaultFlexAppointmentAllowed ? "ALL" : "DISABLED")
}

const contactCustomerIdFromOverride = (
  override?: AppointmentPlanningWorkOrderOverride | null,
): string | null => {
  const value = String(override?.appointment_contact_customer_id ?? "").trim()

  return value || null
}

const planningStrategyFromOverride = ({
  override,
  defaultStrategy,
  hasExistingAppointment = false,
}: {
  override?: AppointmentPlanningWorkOrderOverride | null
  defaultStrategy?: AppointmentPlanningStrategy | null
  hasExistingAppointment?: boolean
}): AppointmentPlanningStrategy => {
  const value = override?.planning_strategy

  if (value === "AUTO" || value === "MANUAL" || value === "KEEP_EXISTING") return value

  if (
    defaultStrategy === "AUTO" ||
    defaultStrategy === "MANUAL" ||
    defaultStrategy === "KEEP_EXISTING"
  ) {
    return defaultStrategy
  }

  return hasExistingAppointment ? "KEEP_EXISTING" : "AUTO"
}

export const resolveReuseAppointmentPlanningStrategy = ({
  row,
  override,
}: {
  row: AppointmentPlanningExistingSourceRow
  override?: AppointmentPlanningWorkOrderOverride | null
}): AppointmentPlanningStrategy =>
  planningStrategyFromOverride({
    override,
    defaultStrategy: row.defaultPlanningStrategy,
    hasExistingAppointment: Boolean(row.planningTargetScheduleEventId || row.previousEvent),
  })

const manualAppointmentTypeFromOverride = (
  override?: AppointmentPlanningWorkOrderOverride | null,
): "HARD" | "FLEX" | null => {
  const value = override?.manual_appointment_type

  return value === "FLEX" ? "FLEX" : value === "HARD" ? "HARD" : null
}

export const buildAppointmentPlanningCommitPayload = ({
  previewPayload,
  previewRows,
}: {
  previewPayload: AppointmentPlanningPreviewPayload
  previewRows: AppointmentPlanningPreviewRow[]
}): AppointmentPlanningCommitPayload => {
  const previewByRowId = new Map(previewRows.map((row) => [row.client_row_id, row]))

  return {
    organization_id: previewPayload.organization_id,
    employee_ids: previewPayload.employee_ids,
    start_date: previewPayload.start_date,
    rows: previewPayload.rows.map((row) => {
      const preview = previewByRowId.get(row.client_row_id)
      const target = preview?.commit_target

      if (!target)
        throw new Error(
          "Für jede ausgewählte Zeile ist ein vollständiger Terminvorschlag erforderlich.",
        )

      const contact =
        preview?.resolved_appointment_contact?.customer_id ??
        row.appointment_contact_customer_id ??
        null

      return {
        client_row_id: row.client_row_id,
        work_order_id: row.work_order_id,
        target,
        appointment_contact_customer_id: contact,
      }
    }),
  }
}

export const buildReuseAppointmentPlanningPreviewPayload = ({
  organizationId,
  rows,
  workOrderOverridesByRowId,
  employeeIds,
  startDate,
  defaultFlexAppointmentAllowed,
  defaultFlexAppointmentMode,
  flexPlanningAvailable,
}: {
  organizationId: string
  rows: AppointmentPlanningExistingSourceRow[]
  workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>
  employeeIds: string[]
  startDate: string
  defaultFlexAppointmentAllowed: boolean
  defaultFlexAppointmentMode?: FlexAppointmentPlanningMode
  flexPlanningAvailable: boolean
}): AppointmentPlanningPreviewPayload => {
  const employeeIdSet = new Set(employeeIds.map(String).filter(Boolean))
  const outputEmployeeIds = [...employeeIds.map(String).filter(Boolean)]
  const payloadRows = rows.map((row) => {
    const override = workOrderOverridesByRowId[row.id]
    const appointmentEmployeeId = String(override?.appointment_employee_id ?? "").trim()
    const planningStrategy = resolveReuseAppointmentPlanningStrategy({ row, override })

    if (planningStrategy === "KEEP_EXISTING") {
      const existingEmployeeId = String(row.previousEvent?.employee_id ?? "").trim()

      if (existingEmployeeId && !outputEmployeeIds.includes(existingEmployeeId)) {
        outputEmployeeIds.push(existingEmployeeId)
      }
    }

    const flexAppointmentMode = resolveRowFlexAppointmentMode({
      override,
      defaultFlexAppointmentAllowed,
      defaultFlexAppointmentMode,
      flexPlanningAvailable,
    })

    return {
      work_order_id: row.workOrder.id,
      client_row_id: row.id,
      expected_active_schedule_event_id:
        row.planningTargetScheduleEventId ??
        row.previousEvent?.id ??
        row.workOrder.current_schedule_event_id ??
        null,
      planning_strategy: planningStrategy,
      manual_appointment_type: manualAppointmentTypeFromOverride(override),
      manual_appointment: override?.manual_appointment ?? override?.manual_flex_appointment ?? null,
      appointment_employee_id:
        appointmentEmployeeId && employeeIdSet.has(appointmentEmployeeId)
          ? appointmentEmployeeId
          : null,
      appointment_contact_customer_id: contactCustomerIdFromOverride(override),
      flex_appointment_allowed: flexAppointmentMode !== "DISABLED",
      flex_appointment_mode: flexAppointmentMode,
    }
  })

  return {
    organization_id: organizationId,
    rows: payloadRows,
    employee_ids: outputEmployeeIds,
    start_date: formatDateOnly(startDate),
    planning_horizon_days: BATCH_PLANNING_HORIZON_DAYS,
    plan_flex_appointments: payloadRows.some((row) => row.flex_appointment_allowed),
  }
}
